"use client"

import React, { useEffect, useId, useRef, useState } from "react"
import { useClickOutside } from "@/app/hooks/useClickOutside"
import { foldForSearch } from "@/app/utils/helpers"
import TripFieldCard from "./TripFieldCard"

interface CountryOption {
    label: string
    value: string | number
}

type CountryCellProps = {
    label: string
    placeholder: string
    options: CountryOption[]
} & (
    | { multiple?: false; value: string; onChange: (code: string) => void }
    | { multiple: true; value: string[]; onChange: (codes: string[]) => void }
)

export default function CountryCell(props: CountryCellProps) {
    const { label, placeholder, options } = props
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState("")
    const [highlightedIndex, setHighlightedIndex] = useState(0)
    const ref = useRef<HTMLDivElement>(null)
    const optionRefs = useRef<(HTMLButtonElement | null)[]>([])
    const listboxId = useId()

    const close = () => {
        setOpen(false)
        setSearch("")
    }

    useClickOutside(ref, close)

    const selectedCodes = props.multiple ? props.value : [props.value].filter(Boolean)
    const labelOf = (code: string) => options.find((option) => String(option.value) === code)?.label
    const firstLabel = labelOf(selectedCodes[0] ?? "")
    const triggerLabel =
        selectedCodes.length > 1 ? `${firstLabel} +${selectedCodes.length - 1}` : firstLabel
    const needle = foldForSearch(search)
    const visible = search
        ? options.filter((option) => foldForSearch(option.label).includes(needle))
        : options

    useEffect(() => {
        if (open) {
            optionRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" })
        }
    }, [highlightedIndex, open])

    // En multi-sélection le panneau reste ouvert : on enchaîne les étapes du séjour.
    const selectOption = (code: string) => {
        if (!props.multiple) {
            props.onChange(code)
            close()
            return
        }
        props.onChange(
            selectedCodes.includes(code) ? selectedCodes.filter((selected) => selected !== code) : [...selectedCodes, code]
        )
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "ArrowDown") {
            event.preventDefault()
            setHighlightedIndex((index) => Math.min(index + 1, visible.length - 1))
        } else if (event.key === "ArrowUp") {
            event.preventDefault()
            setHighlightedIndex((index) => Math.max(index - 1, 0))
        } else if (event.key === "Enter") {
            event.preventDefault()
            const option = visible[highlightedIndex]
            if (option) {
                selectOption(String(option.value))
            }
        } else if (event.key === "Escape") {
            close()
        }
    }

    return (
        <div ref={ref} className="relative min-w-0">
            <TripFieldCard
                label={label}
                value={triggerLabel}
                placeholder={placeholder}
                open={open}
                onToggle={() => setOpen((current) => !current)}
                panelClassName="w-full min-w-[280px] max-w-[85vw]"
            >
                <div className="border-b border-gray-100 p-2">
                    <input
                        autoFocus
                        value={search}
                        onChange={(event) => {
                            setSearch(event.target.value)
                            setHighlightedIndex(0)
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder="Rechercher..."
                        role="combobox"
                        aria-expanded={open}
                        aria-controls={listboxId}
                        aria-activedescendant={visible[highlightedIndex] ? `${listboxId}-${highlightedIndex}` : undefined}
                        className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-[14px] focus:outline-none"
                    />
                </div>
                <div id={listboxId} role="listbox" className="max-h-[240px] overflow-auto py-1">
                    {visible.length === 0 && (
                        <div className="px-3 py-1.5 text-[14px] text-gray-400">Aucun résultat</div>
                    )}
                    {visible.map((option, index) => {
                        const isSelected = selectedCodes.includes(String(option.value))
                        return (
                            <button
                                type="button"
                                key={option.value}
                                id={`${listboxId}-${index}`}
                                role="option"
                                aria-selected={isSelected}
                                ref={(element) => {
                                    optionRefs.current[index] = element
                                }}
                                onMouseEnter={() => setHighlightedIndex(index)}
                                onClick={() => selectOption(String(option.value))}
                                className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[14px] transition-colors ${
                                    index === highlightedIndex ? "bg-rm-blue-pale" : ""
                                } ${isSelected ? "font-semibold text-pale" : "text-gray-900"}`}
                            >
                                <span className="truncate">{option.label}</span>
                                {props.multiple && isSelected && (
                                    <i className="fas fa-check shrink-0 text-[12px] text-rm-orange" aria-hidden />
                                )}
                            </button>
                        )
                    })}
                </div>
            </TripFieldCard>
        </div>
    )
}
